import React, { useEffect, useCallback, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDashboardData } from "@/hooks/useDashboardData";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardStats from "@/components/dashboard/DashboardStats";
import DashboardMainContent from "@/components/dashboard/DashboardMainContent";
import DashboardLoading from "@/components/dashboard/DashboardLoading";
import DashboardError from "@/components/dashboard/DashboardError";
import DashboardAccessMessage from "@/components/dashboard/DashboardAccessMessage";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  PlusCircle,
  UploadCloud,
  ArrowUpCircle,
  ArrowDownCircle,
  CreditCardIcon,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import { fetchMembershipAddOns } from "../store/membership/membershipActions";
import {
  getUserSubscription,
  updateUserProfile,
} from "../store/auth/authActions";
import { usePartsQuota } from "../hooks/usePartsQuota";
import {
  getEffectivePlan,
  isDowngradePending,
} from "../utils/subscriptionUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Copy, ExternalLink, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const BusinessDashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const {
    isAuthenticated,
    user,
    isLoading: isAuthLoading,
    isUserSubscriptionLoading,
    currentSubscription,
  } = useSelector((state) => state.auth);

  const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
  const publicProfileUrl = `${baseUrl}/${
    user?.companyName
      ? encodeURIComponent(user.companyName.replace(/\s+/g, "-").toLowerCase())
      : "seller"
  }`;

  const currentPlan = getEffectivePlan(currentSubscription);
  const [lastPartAddedTimestamp, setLastPartAddedTimestamp] = useState(
    location.state?.timestamp,
  );

  const [contentKey, setContentKey] = useState(Date.now());

  const isSeller = isAuthenticated && user?.role === "seller";
  // A seller is allowed in as long as they are authenticated as a seller.
  // Free plan = no currentSubscription; paid plan = status "active".
  const hasActivePlan =
    isSeller &&
    (currentSubscription?.status === "active" || !currentSubscription);

  const selectedPlanId = currentPlan || location.state?.plan || "free";

  const {
    inventory,
    messages,
    dashboardStats,
    loading: isDataLoading,
    error,
    refreshDashboard,
    isPaginationLoading,
  } = useDashboardData(isSeller && hasActivePlan);

  const { canAddParts } = usePartsQuota();

  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated) {
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => navigate("/dashboard", { replace: true }),
          defaultTabOverride: "login",
        }),
      );
      return;
    }

    if (user?.role !== "seller") {
      toast({
        title: "Access Denied",
        description:
          "This dashboard is for sellers only. Redirecting to your profile...",
        variant: "destructive",
        duration: 3000,
      });
      navigate(user.role === "buyer" ? "/profile" : "/");
      return;
    }

    console.log("user: ", user);
    if (user?.isFirstLogin) {
      dispatch(updateUserProfile({ isFirstLogin: false }));
      toast({
        title: "Welcome!",
        description: "Please check out our membership plans.",
        duration: 5000,
      });
      navigate("/list-inventory");
      return;
    }
  }, [
    isAuthenticated,
    user,
    isAuthLoading,
    hasActivePlan,
    navigate,
    toast,
    dispatch,
  ]);

  useEffect(() => {
    if (
      location.state?.partAdded &&
      location.state?.timestamp !== lastPartAddedTimestamp
    ) {
      (async () => {
        if (refreshDashboard) {
          await refreshDashboard();
          toast({
            title: "Inventory Updated",
            description: "Your dashboard reflects the latest changes.",
            duration: 2500,
          });

          setLastPartAddedTimestamp(location.state.timestamp);
          setContentKey(Date.now());
          navigate(location.pathname, {
            replace: true,
            state: { ...location.state, partAdded: false },
          });
        }
      })();
    }
  }, [
    location.state,
    refreshDashboard,
    navigate,
    location.pathname,
    toast,
    lastPartAddedTimestamp,
  ]);

  useEffect(() => {
    dispatch(fetchMembershipAddOns());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user?.uid) {
      dispatch(getUserSubscription(user?.uid));
    }
  }, []);

  // Removed: free-plan sellers (no membership.plan yet) were being
  // redirected to /list-inventory, preventing dashboard access.
  // Free plan is valid — users can view and add up to 10 parts.

  const handleRefreshDashboard = useCallback(async () => {
    if (!isSeller || !hasActivePlan) {
      toast({
        title: "Cannot Refresh",
        description: "You must be logged in as a seller with an active plan.",
        variant: "destructive",
      });
      return;
    }
    if (refreshDashboard) {
      await refreshDashboard();
      setContentKey(Date.now());
      toast({
        title: "Dashboard Refreshed",
        description: "Inventory data has been updated.",
        duration: 2000,
      });
    }
  }, [refreshDashboard, toast, isSeller, hasActivePlan]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  // --- Conditional Rendering ---
  if (isAuthLoading || isUserSubscriptionLoading) {
    return <DashboardLoading message="Verifying authentication..." />;
  }

  if (!isSeller || !hasActivePlan) {
    return <DashboardAccessMessage isAuthenticated={isAuthenticated} />;
  }

  if ((isDataLoading || isUserSubscriptionLoading) && !error) {
    return <DashboardLoading message="Loading your dashboard data..." />;
  }

  if (error) {
    return <DashboardError error={error} onRetry={handleRefreshDashboard} />;
  }

  return (
    <motion.div
      className="space-y-8 w-full bg-gradient-to-br from-background to-secondary/5 min-h-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <DashboardHeader
        variants={itemVariants}
        userName={user?.companyName || user?.name || user?.email || "Seller"}
      />
      <motion.div
        variants={itemVariants}
        className="flex flex-col xs:flex-row justify-center md:justify-end items-center gap-4 md:px-6 lg:px-8 w-full xs:w-auto"
      >
        <Button
          asChild
          variant="default"
          size="sm"
          className={`${
            isDowngradePending(currentSubscription)
              ? "bg-gradient-to-r from-purple-500 to-indigo-600"
              : currentSubscription?.planId == "premium"
                ? "bg-gradient-to-r from-accent to-orange-600"
                : "bg-gradient-to-r from-purple-500 to-indigo-600"
          } text-white hover:opacity-90 w-full xs:w-auto`}
        >
          <Link to="/list-inventory">
            {!isDowngradePending(currentSubscription) ? (
              <>
                {currentSubscription?.planId == "premium" ? (
                  <ArrowDownCircle className="mr-2 h-4 w-4" />
                ) : (
                  <ArrowUpCircle className="mr-2 h-4 w-4" />
                )}
              </>
            ) : (
              <CreditCardIcon className="mr-2 h-4 w-4" />
            )}
            {isDowngradePending(currentSubscription)
              ? "Update Plan"
              : currentSubscription?.planId == "premium"
                ? "Downgrade Plan"
                : "Upgrade Plan"}
          </Link>
        </Button>
        <Button
          onClick={handleRefreshDashboard}
          variant="outline"
          size="sm"
          disabled={isDataLoading || isPaginationLoading}
          className="w-full xs:w-auto"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${
              isDataLoading || isPaginationLoading ? "animate-spin" : ""
            }`}
          />
          Refresh Dashboard
        </Button>
      </motion.div>

      <motion.div variants={itemVariants}>
        <DashboardStats
          inventoryCount={dashboardStats?.totalParts || 0}
          messageCount={dashboardStats?.totalUnreadMessages || 0}
          totalSales={dashboardStats?.totalSales || 0}
          salesTrend={dashboardStats?.salesTrend || 0}
          planTier={[
            { currentPlan: dashboardStats?.currentPlan || selectedPlanId },
            // Only surface previousPlan when a downgrade is actually pending —
            // not on upgrades, which also populate previousPlanId on the subscription.
            {
              previousPlan: isDowngradePending(currentSubscription)
                ? dashboardStats?.previousPlan
                : null,
            },
          ]}
          partLimit={currentSubscription?.quota?.partsLimit}
          messageLimit={currentSubscription?.quota?.messenger}
        />
      </motion.div>
      {/* Seller Public Page Link */}
      {currentPlan == "premium" && (
        <div className="flex justify-end items-end px-6 md:px-8">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 border-primary text-primary font-semibold hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 rounded-lg shadow-sm"
              >
                <ExternalLink className="w-4 h-4" />
                View Business Site
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-lg p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-800">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white text-center">
                  🌐 Your Public Business Profile
                </DialogTitle>
                <DialogDescription className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Share this personalized link to let others view your company
                  profile and listed parts.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
                <Input
                  readOnly
                  value={publicProfileUrl}
                  className="font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary transition-all rounded-lg w-full text-center sm:text-left"
                />
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  className="bg-primary hover:bg-accent text-white shadow-md transition-transform duration-300 hover:scale-105"
                  onClick={() => {
                    navigator.clipboard.writeText(publicProfileUrl);
                    toast({
                      title: "Copied!",
                      description: "Public profile link copied to clipboard.",
                      duration: 2000,
                    });
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <div className="mt-6 flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => window.open(publicProfileUrl, "_blank")}
                  className="flex items-center gap-2 border-gray-300 dark:border-gray-700 hover:bg-primary hover:text-white transition-colors px-6"
                >
                  <ExternalLink className="w-4 h-4" /> Open Link
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <DashboardMainContent
        key={contentKey}
        inventory={inventory}
        variants={itemVariants}
        isLoading={isDataLoading}
        onRefreshInventory={handleRefreshDashboard}
      />
      {/* 
      {canAddParts && (
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row justify-center items-center pt-4 pb-8 gap-4"
        >
          <Button
            size="lg"
            onClick={() => navigate("/dashboard/add-part")}
            className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity text-primary-foreground shadow-lg px-8 py-2 sm:py-6 text-sm sm:text-base w-full sm:w-auto"
          >
            <PlusCircle className="mr-2 h-5 w-5" /> Add Single Part
          </Button>
          {getEffectivePlan(currentSubscription) === "premium" && (
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/dashboard/bulk-add-parts")}
              className="border-primary hover:border-accent text-primary hover:text-accent hover:bg-accent/10 transition-colors shadow-lg px-8 py-2 sm:py-6 text-sm sm:text-base w-full sm:w-auto"
            >
              <UploadCloud className="mr-2 h-5 w-5" /> Bulk Add Parts
            </Button>
          )}
        </motion.div>
      )} */}
    </motion.div>
  );
};

export default BusinessDashboardPage;
